import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
   LuArrowLeftRight,
   LuBadgeCheck,
   LuCheck,
   LuChevronUp,
   LuFileText,
   LuInfo,
   LuQrCode,
   LuX,
} from 'react-icons/lu';

import {
   CardHint,
   CardTitle,
   DarkTable,
   EmptyText,
   EmptyTitle,
   EmptyWrap,
   Pill,
   RowIconButton,
   RowIconLink,
   RowSettleButton,
   SecondaryButton,
   SkeletonBlock,
   TableScroll,
} from '../../../../styles/dunning/base';
import { AvatarOnDark } from '../../../../styles/dunning/kanban';
import { TextLink } from '../../../../styles/ui/Link';
import {
   Collapsible,
   CollapseToggle,
   CommitValue,
   DRAWER_ANIM_MS,
   DrawerBackdrop,
   DrawerBody,
   DrawerCard,
   DrawerCardPad,
   DrawerClose,
   DrawerDoc,
   DrawerHead,
   DrawerIdentity,
   DrawerMetaDivider,
   DrawerMetaRow,
   DrawerName,
   DrawerPanel,
   DrawerStat,
   DrawerStatLabel,
   DrawerStatValue,
   DrawerStatsRow,
   DrawerStatsSpacer,
   DrawerTitleRow,
   HeadAssignButton,
   HeadCommitButton,
   SectionHeadRow,
   TimelineBody,
   TimelineContent,
   TimelineDot,
   TimelineItem,
   TimelineLine,
   TimelineMeta,
   TimelineRail,
   TimelineTitle,
   TimelineTitleRow,
} from '../../../../styles/dunning/drawer';
import {
   agingInfo,
   buildTimeline,
   caseFigures,
   commitHeader,
   isExternalTrack,
   memberStatusPill,
   needsAssignee,
} from '../utils/derive';
import {
   formatBRL,
   formatDateBR,
   formatDoc,
   formatTimestamp,
   getInitials,
} from '../../utils/dunningFormat';
import InteractionForm from './InteractionForm';
import AttachmentChips from './AttachmentChips';
import CheckChargeModal from '../../components/CheckChargeModal';
import PixModal from './PixModal';
import SettleModal from './SettleModal';
import ReasonConfirmModal from '../../../../components/ReasonConfirmModal';
import { deleteDunningFile } from '../../../../services/finance/dunningApi';
import { financeErrorMessage } from '../../utils/apiError';
import {
   ATTACHMENT_REASON_MAX,
   ATTACHMENT_REASON_MIN,
   TRACK_LABELS,
} from '../constants';

// ---------------------------------------------------------------------------
// Drawer do caso: cabeçalho escuro (identidade + números + compromisso, com o
// "+ Novo compromisso" ao lado), títulos com ações por linha (boleto/PIX/
// baixa), formulário de tratativa e histórico (merge interações + eventos).
//
// A baixa manual NUNCA é otimista: o título confirmado entra em "Aguardando
// webhook" (`awaitingCharges`, estado da sessão) e o container faz polling até
// o webhook do Asaas refletir no caso.
//
// RBAC fatiado (0100): `canInteract` libera o trabalho do caso — tratativa,
// anexo, compromisso. `canUpdate` é gestão — só o gatilho de responsável e o
// select "Avançar para a raia" (repassado ao form como `canMoveStage`).
//
// Trilha (0103): o mesmo bloco serve "Enviar ao escritório" (caso interno) e
// "Devolver à régua" (caso externo) — os dois abrem a seleção de títulos, e a
// escrita acontece só na confirmação do modal.
// ---------------------------------------------------------------------------

// Aviso no lugar do formulário de tratativa. `TRANSFERRED` (0103) não é nem
// recebido nem cancelado: os títulos mudaram de trilha — dizer "caso
// cancelado" ali seria mentira.
//
// E, ao contrário dos outros dois, ele **não é terminal** (D21, adendo do 0103
// em 2026-08-29): o par interno+externo é o episódio, e o caso ESTACIONADO
// volta a `OPEN` — na raia onde parou — enquanto houver título vencido em
// alguma das duas pontas, sem ação de ninguém. O texto precisa dizer isso,
// senão o operador lê como fim de linha e abre um caso novo por engano.
const CLOSED_CASE_NOTICE = {
   SETTLED: 'Cobrança recebida, nada mais a fazer por aqui.',
   CANCELLED: 'Caso cancelado, nada mais a fazer por aqui.',
   TRANSFERRED:
      'Caso estacionado: todos os títulos estão na outra trilha. Ele volta a ficar em cobrança sozinho, na raia em que parou, se um título retornar ou vencer.',
};

// Identificação curta do título — mesma forma usada na coluna "Parcela".
function memberLabel(member) {
   if (!member) return null;
   return member.charge?.installment_number
      ? `Parcela ${member.charge.installment_number}/${member.charge.installment_count}`
      : 'Título avulso';
}

// Quem conferiu, quando e com que observação. `checked_by` só vem no GET /:id
// (0103 §4.2) — que é justamente o que alimenta este drawer; no kanban existe
// só o booleano.
function checkTooltip(member) {
   return [
      `Conferido em ${formatTimestamp(member.checked_at)}`,
      member.checked_by?.nome ? `por ${member.checked_by.nome}` : null,
      member.checked_note,
   ]
      .filter(Boolean)
      .join(' · ');
}

export default function CaseDrawer({
   caseData = null,
   events,
   loading,
   stages,
   activeStages,
   today,
   canInteract,
   canUpdate,
   canExternal,
   canSettle,
   canAssign,
   canReconcile,
   canReadAll,
   awaitingCharges,
   onClose,
   onOpenCase,
   onCaseUpdated,
   onOpenCommit,
   onOpenAssign,
   onAskTransfer,
   onSettleConfirmed,
}) {
   // Seções recolhíveis (2026-08-31): o drawer é uma pilha longa e o histórico
   // — o que mais cresce — fica no fim. Estado local e efêmero de propósito:
   // é preferência de leitura do momento, não recorte de trabalho (não vai
   // para a URL, ao contrário do caso aberto).
   //
   // Nascem FECHADAS (decisão 2026-08-31): a abertura do caso mostra o
   // cabeçalho + as duas seções dobradas + o histórico, que é o que se quer ler
   // primeiro. Quem vai operar expande o que precisa.
   const [showCharges, setShowCharges] = useState(false);
   const [pixMember, setPixMember] = useState(null);
   const [settleMember, setSettleMember] = useState(null);
   // Título em vias de ser conferido/desconferido no acerto (0103).
   const [checkMember, setCheckMember] = useState(null);
   // Anexo em vias de ser excluído: { interactionId, file }. O motivo é
   // obrigatório (3–255) e vem do modal compartilhado.
   const [fileToDelete, setFileToDelete] = useState(null);
   const [isDeletingFile, setIsDeletingFile] = useState(false);

   // Deslize lateral nos dois sentidos: monta fechado → abre no frame
   // seguinte; no fechar, anima a saída e só então avisa o container (que
   // desmonta). O timer é limpo se o container desmontar antes.
   const [isShown, setIsShown] = useState(false);
   const closeTimerRef = useRef(null);

   useEffect(() => {
      const openTimer = window.setTimeout(() => setIsShown(true), 0);
      return () => {
         window.clearTimeout(openTimer);
         if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
      };
   }, []);

   // Trocar de caso conta como "abrir um caso": o drawer NÃO desmonta quando o
   // container troca o `openCaseId` (é o que acontece no link de contraparte da
   // timeline, §D22), então sem isto a seção ficaria aberta do caso anterior.
   useEffect(() => {
      setShowCharges(false);
   }, [caseData?.id]);

   const handleClose = useCallback(() => {
      if (closeTimerRef.current) return;
      setIsShown(false);
      closeTimerRef.current = window.setTimeout(onClose, DRAWER_ANIM_MS);
   }, [onClose]);

   const timeline = useMemo(
      () =>
         buildTimeline({
            interactions: caseData?.interactions,
            events,
            stages,
         }),
      [caseData, events, stages]
   );

   // Exclusão de anexo: responde o caso inteiro (como toda mutação do módulo)
   // — aplica por cima e o chip vira rastro na hora, sem refetch.
   const handleConfirmDeleteFile = useCallback(
      async (reason) => {
         if (!fileToDelete || !caseData) return;
         setIsDeletingFile(true);
         try {
            const updated = await deleteDunningFile(
               caseData.id,
               fileToDelete.interactionId,
               fileToDelete.file.id,
               { reason }
            );
            onCaseUpdated(updated);
            toast.success('Anexo excluído.');
            setFileToDelete(null);
         } catch (err) {
            // Modal segue aberto: o motivo digitado não se perde.
            toast.error(
               financeErrorMessage(err, 'Não foi possível excluir o anexo.')
            );
         } finally {
            setIsDeletingFile(false);
         }
      },
      [fileToDelete, caseData, onCaseUpdated]
   );

   const aging = caseData ? agingInfo(caseData.oldest_due_date, today) : null;
   const commit = caseData ? commitHeader(caseData, today) : null;
   // Bloco de números do cabeçalho por status do caso (0102) — mesmo corte do
   // card do kanban.
   const figures = caseData ? caseFigures(caseData) : null;

   // Trilha: o bloco de transferência serve os DOIS sentidos e é decidido pelo
   // `track` do caso, não pela raia (0103). Caso encerrado não transfere nada.
   const isExternal = isExternalTrack(caseData);
   // A raia EXTERNAL já se chama "Cobrança administrativa" — com o caso parado
   // nela, o selo de trilha só duplicaria a pill da raia (ver o cabeçalho).
   const isExternalStage = caseData?.stage?.stage_type === 'EXTERNAL';
   // A conferência do acerto só existe sobre título PAGO. Sem nenhum membro
   // SETTLED, a coluna seria uma fileira de "—" que nunca vira nada neste
   // estágio (ajuste 2026-08-30). Caso MISTO (parte paga, parte em aberto)
   // continua com a coluna — é ali que o gestor confere o que já entrou.
   const hasSettledMember = (caseData?.members ?? []).some(
      (member) => member.member_status === 'SETTLED'
   );
   const showCheckColumn = isExternal && hasSettledMember;
   const showTransfer = !!caseData && canExternal && caseData.status === 'OPEN';
   const transferCopy = isExternal
      ? {
           title: 'Régua interna',
           hint: 'Devolver títulos para o time interno trabalhar novamente.',
           action: 'Devolver à régua',
        }
      : {
           title: 'Cobrança administrativa',
           hint: 'Selecione os títulos que vão para cobrança externa.',
           action: 'Enviar ao escritório',
        };

   return (
      <>
         <DrawerBackdrop $show={isShown} onClick={handleClose} />
         <DrawerPanel
            $show={isShown}
            role="dialog"
            aria-modal="true"
            aria-label="Caso de cobrança"
         >
            {!caseData ? (
               <DrawerBody>
                  {loading ? (
                     <>
                        <SkeletonBlock $h="120px" $radius="12px" />
                        <SkeletonBlock $h="180px" $radius="12px" />
                        <SkeletonBlock $h="220px" $radius="12px" />
                     </>
                  ) : (
                     <EmptyWrap>
                        <EmptyTitle>Algo deu errado</EmptyTitle>
                        <EmptyText>Falha ao carregar o caso.</EmptyText>
                        <div style={{ marginTop: 12 }}>
                           <SecondaryButton type="button" onClick={handleClose}>
                              Fechar
                           </SecondaryButton>
                        </div>
                     </EmptyWrap>
                  )}
               </DrawerBody>
            ) : (
               <>
                  <DrawerHead>
                     {/* Fechar à ESQUERDA (decisão 2026-08-14): o X fica na
                         borda por onde o painel "sai" da tela. */}
                     <DrawerTitleRow>
                        <DrawerClose
                           type="button"
                           title="Fechar"
                           onClick={handleClose}
                        >
                           <LuX size={16} />
                        </DrawerClose>
                        <DrawerIdentity>
                           <DrawerName>{caseData.customer_name}</DrawerName>
                           <DrawerDoc>
                              {formatDoc(caseData.customer_doc)} · caso #
                              {caseData.id}
                           </DrawerDoc>
                        </DrawerIdentity>
                     </DrawerTitleRow>

                     <DrawerStatsRow>
                        <DrawerStat>
                           <DrawerStatLabel>
                              {figures.amountLabel}
                           </DrawerStatLabel>
                           <DrawerStatValue>
                              {formatBRL(figures.amount)}
                           </DrawerStatValue>
                        </DrawerStat>
                        <DrawerStat>
                           <DrawerStatLabel>Títulos</DrawerStatLabel>
                           <DrawerStatValue>{figures.count}</DrawerStatValue>
                        </DrawerStat>
                        {/* Terceiro número por status (0102): atraso só existe
                            no caso aberto; no recebido dá lugar à data do
                            pagamento; no cancelado não há terceiro. */}
                        {figures.showAging && (
                           <DrawerStat>
                              <DrawerStatLabel>Atraso maior</DrawerStatLabel>
                              <DrawerStatValue>
                                 {aging.days === null ? '—' : `${aging.days}d`}
                              </DrawerStatValue>
                           </DrawerStat>
                        )}
                        {figures.date && (
                           <DrawerStat>
                              <DrawerStatLabel>
                                 {figures.dateLabel}
                              </DrawerStatLabel>
                              <DrawerStatValue>
                                 {formatDateBR(figures.date)}
                              </DrawerStatValue>
                           </DrawerStat>
                        )}
                        <DrawerStatsSpacer />
                        {/* Trilha (0103): o caso do escritório é outro caso, com
                            outras regras (não muda de raia, aceita conferência).
                            Sem o selo, dois casos do mesmo cliente no board
                            ficam indistinguíveis.

                            Só aparece quando NÃO repete a raia ao lado
                            (ajuste 2026-08-30): caso externo ABERTO vive na
                            raia EXTERNAL, cujo nome já é "Cobrança
                            administrativa" — duas pills idênticas lado a lado.
                            Quando o título é pago, o webhook move o caso para
                            "Recebido" e o selo volta a ser a ÚNICA indicação de
                            que aquele caso é do escritório. */}
                        {isExternal && !isExternalStage && (
                           <Pill
                              $variant="onDark"
                              title="Este caso está com o escritório de cobrança"
                              style={{ fontSize: 11, padding: '5px 12px' }}
                           >
                              <LuArrowLeftRight size={11} />
                              {TRACK_LABELS.EXTERNAL}
                           </Pill>
                        )}
                        {/* Raia terminal ganha o verde do tipo (semântica por
                            stage_type, nunca pelo nome editável). */}
                        <Pill
                           $variant={
                              caseData.stage?.stage_type === 'TERMINAL_PAID'
                                 ? 'successOnDark'
                                 : 'onDark'
                           }
                           style={{ fontSize: 11, padding: '5px 12px' }}
                        >
                           {caseData.stage?.name ?? '—'}
                        </Pill>
                     </DrawerStatsRow>

                     <DrawerMetaRow>
                        {/* Reatribuição independente do compromisso: só quem
                            pode atribuir (read_all + update, já combinados em
                            `canAssign`) vê o gatilho; os demais seguem com o
                            texto estático. */}
                        {canAssign && caseData.status === 'OPEN' ? (
                           <HeadAssignButton
                              type="button"
                              title="Atribuir responsável"
                              onClick={onOpenAssign}
                           >
                              <AvatarOnDark
                                 $size={22}
                                 $empty={!caseData.assigned_user}
                              >
                                 {getInitials(caseData.assigned_user?.nome)}
                              </AvatarOnDark>
                              {caseData.assigned_user?.nome ??
                                 'Sem responsável'}
                           </HeadAssignButton>
                        ) : (
                           <span
                              style={{
                                 display: 'inline-flex',
                                 alignItems: 'center',
                                 gap: 7,
                              }}
                           >
                              <AvatarOnDark
                                 $size={22}
                                 $empty={!caseData.assigned_user}
                              >
                                 {getInitials(caseData.assigned_user?.nome)}
                              </AvatarOnDark>
                              {caseData.assigned_user?.nome ??
                                 'Sem responsável'}
                           </span>
                        )}
                        {/* §9: caso do escritório sem responsável é trabalho
                            que o escritório NÃO enxerga (ele não tem read_all).
                            O aviso mora ao lado do dado que resolve. */}
                        {needsAssignee(caseData) && (
                           <Pill
                              $variant="warn"
                              title="O escritório só enxerga casos atribuídos a ele"
                           >
                              aguardando atribuição
                           </Pill>
                        )}
                        {/* Caso encerrado não tem próximo passo — o backend
                            manda `action_status: null` por contrato e o
                            "Compromisso —" só ocupava espaço (0102 §3). */}
                        {caseData.status === 'OPEN' && (
                           <>
                              <DrawerMetaDivider>·</DrawerMetaDivider>
                              <span>
                                 Compromisso{' '}
                                 <CommitValue
                                    $tone={
                                       commit.tone === 'normal'
                                          ? undefined
                                          : commit.tone
                                    }
                                 >
                                    {commit.text}
                                 </CommitValue>
                              </span>
                           </>
                        )}
                        {/* Agendar compromisso mora no cabeçalho, junto do
                            dado que altera — separado do registro de
                            tratativa (decisão 2026-08-17). */}
                        {canInteract && caseData.status === 'OPEN' && (
                           <HeadCommitButton
                              type="button"
                              onClick={onOpenCommit}
                           >
                              + Novo compromisso
                           </HeadCommitButton>
                        )}
                     </DrawerMetaRow>
                  </DrawerHead>

                  <DrawerBody>
                     {/* ─── Títulos do caso ─────────────────────────── */}
                     <DrawerCard>
                        <SectionHeadRow
                           style={{
                              padding: '12px 14px',
                              borderBottom: showCharges
                                 ? '1px solid rgba(0,0,0,0.07)'
                                 : '1px solid transparent',
                              transition: 'border-color 220ms ease',
                           }}
                        >
                           <CardTitle style={{ fontSize: 14, flex: 1 }}>
                              Títulos do caso
                           </CardTitle>
                           <CardHint style={{ fontSize: 11 }}>
                              {caseData.open_count} em aberto de{' '}
                              {(caseData.members ?? []).length}
                           </CardHint>
                           <CollapseToggle
                              type="button"
                              $open={showCharges}
                              aria-expanded={showCharges}
                              aria-controls="dunning-charges-section"
                              title={
                                 showCharges
                                    ? 'Recolher títulos do caso'
                                    : 'Expandir títulos do caso'
                              }
                              onClick={() => setShowCharges((prev) => !prev)}
                           >
                              <LuChevronUp size={15} />
                           </CollapseToggle>
                        </SectionHeadRow>
                        <Collapsible
                           id="dunning-charges-section"
                           $open={showCharges}
                        >
                        {/* Div intermediária de propósito: o `Collapsible`
                            impõe `overflow: hidden` ao filho DIRETO (é o que
                            permite espremer a altura), e o filho direto não
                            pode ser o `TableScroll` — ele precisa do
                            `overflow-x: auto` para a tabela rolar na largura
                            de 620px do drawer. */}
                        <div>
                        <TableScroll>
                           <DarkTable>
                              <thead>
                                 <tr>
                                    <th>Parcela</th>
                                    <th>Vencimento</th>
                                    <th className="num">Atraso</th>
                                    <th className="num">Valor</th>
                                    <th>Situação</th>
                                    {/* Acerto (0103): só na trilha externa E só
                                        quando há título pago para conferir. */}
                                    {showCheckColumn && <th>Conferido</th>}
                                    <th className="num" aria-label="Ações" />
                                 </tr>
                              </thead>
                              <tbody>
                                 {(caseData.members ?? []).map((member) => {
                                    const isOpen =
                                       member.member_status === 'OPEN';
                                    // Acerto só existe sobre título pago.
                                    const isSettledMember =
                                       member.member_status === 'SETTLED';
                                    const isAwaiting =
                                       !!awaitingCharges[member.charge_id] &&
                                       isOpen;
                                    const memberAging = agingInfo(
                                       isOpen
                                          ? member.charge?.due_date
                                          : null,
                                       today
                                    );
                                    const situation = memberStatusPill(
                                       member,
                                       isAwaiting
                                    );
                                    const settleBlocked = !isOpen || isAwaiting;

                                    let settleTitle = 'Dar baixa manual';
                                    if (!isOpen) {
                                       settleTitle = 'Título já encerrado';
                                    } else if (isAwaiting) {
                                       settleTitle =
                                          'Baixa enviada, aguardando webhook';
                                    }

                                    return (
                                       <tr key={member.id}>
                                          <td>
                                             <div style={{ fontWeight: 700 }}>
                                                {member.charge
                                                   ?.installment_number
                                                   ? `${member.charge.installment_number}/${member.charge.installment_count}`
                                                   : 'Avulso'}
                                             </div>
                                             <div
                                                style={{
                                                   fontSize: 11,
                                                   color: '#8a8a8a',
                                                }}
                                             >
                                                {member.charge?.order_number
                                                   ? `Pedido ${member.charge.order_number}`
                                                   : '—'}
                                             </div>
                                          </td>
                                          <td
                                             style={{
                                                fontVariantNumeric:
                                                   'tabular-nums',
                                             }}
                                          >
                                             {formatDateBR(
                                                member.charge?.due_date
                                             )}
                                          </td>
                                          <td className="num">
                                             <Pill
                                                $variant={memberAging.variant}
                                             >
                                                {memberAging.days === null
                                                   ? '—'
                                                   : `${memberAging.days} dias`}
                                             </Pill>
                                          </td>
                                          <td
                                             className="num"
                                             style={{
                                                fontWeight: 700,
                                                fontVariantNumeric:
                                                   'tabular-nums',
                                             }}
                                          >
                                             {formatBRL(member.charge?.value)}
                                          </td>
                                          <td>
                                             <Pill $variant={situation.variant}>
                                                {situation.label}
                                             </Pill>
                                          </td>
                                          {showCheckColumn && (
                                             <td>
                                                {/* Só título PAGO entra no
                                                    acerto (422
                                                    check_requires_settled_member)
                                                    — no resto, traço. Isto só
                                                    aparece em caso MISTO: sem
                                                    nenhum pago, a coluna
                                                    inteira não é renderizada. */}
                                                {!isSettledMember && '—'}
                                                {isSettledMember &&
                                                   (member.checked ? (
                                                      <Pill
                                                         $variant="success"
                                                         title={checkTooltip(
                                                            member
                                                         )}
                                                      >
                                                         <LuBadgeCheck
                                                            size={11}
                                                         />
                                                         conferido
                                                      </Pill>
                                                   ) : (
                                                      <Pill $variant="muted">
                                                         não conferido
                                                      </Pill>
                                                   ))}
                                             </td>
                                          )}
                                          <td className="num">
                                             <div
                                                style={{
                                                   display: 'flex',
                                                   gap: 6,
                                                   justifyContent: 'flex-end',
                                                }}
                                             >
                                                {member.charge?.boleto_url ? (
                                                   <RowIconLink
                                                      href={
                                                         member.charge
                                                            .boleto_url
                                                      }
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      title="Boleto (PDF Asaas)"
                                                   >
                                                      <LuFileText size={15} />
                                                   </RowIconLink>
                                                ) : (
                                                   <RowIconButton
                                                      type="button"
                                                      disabled
                                                      title="Boleto indisponível"
                                                   >
                                                      <LuFileText size={15} />
                                                   </RowIconButton>
                                                )}
                                                <RowIconButton
                                                   type="button"
                                                   title="PIX copia e cola"
                                                   onClick={() =>
                                                      setPixMember(member)
                                                   }
                                                >
                                                   <LuQrCode size={15} />
                                                </RowIconButton>
                                                {canSettle && (
                                                   <RowSettleButton
                                                      type="button"
                                                      title={settleTitle}
                                                      disabled={settleBlocked}
                                                      onClick={() =>
                                                         setSettleMember(
                                                            member
                                                         )
                                                      }
                                                   >
                                                      <LuCheck size={15} />
                                                   </RowSettleButton>
                                                )}
                                                {/* Conferir no acerto: só na
                                                    trilha externa, só título
                                                    pago, só com :reconcile.
                                                    Segue disponível em caso
                                                    ENCERRADO — o acerto é
                                                    depois do pagamento. */}
                                                {isExternal &&
                                                   canReconcile &&
                                                   isSettledMember && (
                                                      <RowIconButton
                                                         type="button"
                                                         title={
                                                            member.checked
                                                               ? 'Desfazer conferência'
                                                               : 'Marcar como conferido'
                                                         }
                                                         onClick={() =>
                                                            setCheckMember(
                                                               member
                                                            )
                                                         }
                                                      >
                                                         <LuBadgeCheck
                                                            size={15}
                                                         />
                                                      </RowIconButton>
                                                   )}
                                             </div>
                                          </td>
                                       </tr>
                                    );
                                 })}
                              </tbody>
                           </DarkTable>
                        </TableScroll>
                        </div>
                        </Collapsible>
                     </DrawerCard>

                     {/* ─── Registrar tratativa ─────────────────────────
                         Caso fechado não expõe o formulário: o backend já
                         barra tudo com 422 dunning_not_open, mas mostrar
                         inputs mortos convida ao erro. Vira aviso informativo
                         (decisão 2026-08-14). */}
                     {caseData.status !== 'OPEN' ? (
                        <DrawerCardPad
                           style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 10,
                           }}
                        >
                           <LuInfo size={16} color="#8a8a8a" />
                           <CardHint style={{ fontSize: 13, color: '#666' }}>
                              {CLOSED_CASE_NOTICE[caseData.status] ??
                                 CLOSED_CASE_NOTICE.CANCELLED}
                           </CardHint>
                        </DrawerCardPad>
                     ) : (
                        canInteract && (
                           <InteractionForm
                              caseData={caseData}
                              activeStages={activeStages}
                              canMoveStage={canUpdate}
                              onSubmitted={onCaseUpdated}
                           />
                        )
                     )}

                     {/* ─── Trilha: enviar ao escritório / devolver ─── */}
                     {showTransfer && (
                        <DrawerCardPad
                           style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 12,
                           }}
                        >
                           <div style={{ flex: 1, minWidth: 200 }}>
                              <CardTitle
                                 style={{ fontSize: 13, display: 'block' }}
                              >
                                 {transferCopy.title}
                              </CardTitle>
                              <CardHint>{transferCopy.hint}</CardHint>
                           </div>
                           {/* Deixou de ser destrutivo: a transferência tem
                               volta pelo caminho inverso (0103 §5.2), e quem
                               grava de fato é o Confirmar do modal (FC7 G.1). */}
                           <SecondaryButton
                              type="button"
                              onClick={onAskTransfer}
                           >
                              <LuArrowLeftRight size={15} />
                              {transferCopy.action}
                           </SecondaryButton>
                        </DrawerCardPad>
                     )}

                     {/* ─── Histórico ───────────────────────────────── */}
                     <DrawerCardPad>
                        <CardTitle style={{ fontSize: 14 }}>
                           Histórico
                        </CardTitle>
                        <div>
                           {timeline.map((item, index) => (
                              <TimelineItem key={item.key}>
                                 <TimelineRail>
                                    <TimelineDot $tone={item.dotTone} />
                                    <TimelineLine
                                       $last={index === timeline.length - 1}
                                    />
                                 </TimelineRail>
                                 <TimelineContent>
                                    <TimelineTitleRow>
                                       <TimelineTitle>
                                          {item.title}
                                       </TimelineTitle>
                                       <Pill $variant={item.tagVariant}>
                                          {item.tag}
                                       </Pill>
                                    </TimelineTitleRow>
                                    {item.body && (
                                       <TimelineBody>
                                          {item.body}
                                          {/* Elo entre as pontas da
                                              transferência (D22): sem ele, a
                                              linha diz "Título #265 enviado" e
                                              o rastro morre ali — o trabalho
                                              feito na outra trilha fica
                                              inalcançável pela tela.

                                              Só para quem tem `read_all`: o
                                              caso da outra ponta pode não ser
                                              do usuário restrito, e o 403
                                              `dunning_not_assigned` é
                                              interceptado globalmente e
                                              derrubaria a tela em /403. */}
                                          {canReadAll && item.counterpart && (
                                             <>
                                                {' '}
                                                <TextLink
                                                   as="button"
                                                   type="button"
                                                   title="Abrir o caso da outra ponta da transferência"
                                                   onClick={() =>
                                                      onOpenCase(
                                                         item.counterpart.id
                                                      )
                                                   }
                                                >
                                                   {item.counterpart.label}
                                                </TextLink>
                                             </>
                                          )}
                                       </TimelineBody>
                                    )}
                                    {/* Anexos da tratativa (0096): 4ª linha da
                                        hierarquia, entre o relato e a
                                        assinatura. Evento nunca tem. */}
                                    {item.files?.length > 0 && (
                                       <AttachmentChips
                                          caseId={caseData.id}
                                          interactionId={item.interactionId}
                                          files={item.files}
                                          canDelete={
                                             canInteract &&
                                             caseData.status === 'OPEN'
                                          }
                                          onAskDelete={(
                                             interactionId,
                                             file
                                          ) =>
                                             setFileToDelete({
                                                interactionId,
                                                file,
                                             })
                                          }
                                       />
                                    )}
                                    <TimelineMeta>
                                       {[...item.meta, formatTimestamp(item.ts)]
                                          .filter(Boolean)
                                          .join(' · ')}
                                    </TimelineMeta>
                                 </TimelineContent>
                              </TimelineItem>
                           ))}
                           {timeline.length === 0 && (
                              <CardHint>Sem movimentações registradas.</CardHint>
                           )}
                        </div>
                     </DrawerCardPad>
                  </DrawerBody>
               </>
            )}
         </DrawerPanel>

         <PixModal
            isOpen={!!pixMember}
            member={pixMember}
            onClose={() => setPixMember(null)}
         />
         <SettleModal
            isOpen={!!settleMember}
            member={settleMember}
            onClose={() => setSettleMember(null)}
            onConfirmed={onSettleConfirmed}
         />
         {/* Acerto: o modal é compartilhado com o relatório, então recebe o
             título já achatado — ele não conhece o shape de `member`. */}
         <CheckChargeModal
            isOpen={!!checkMember}
            dunningId={caseData?.id ?? null}
            chargeId={checkMember?.charge_id ?? null}
            checked={!!checkMember?.checked}
            label={memberLabel(checkMember)}
            value={checkMember?.charge?.value ?? null}
            paymentDate={checkMember?.charge?.payment_date ?? null}
            onClose={() => setCheckMember(null)}
            onConfirmed={onCaseUpdated}
         />

         {/* Exclusão de anexo — motivo obrigatório, molde compartilhado com a
             exclusão de movimento. O aviso é forte de propósito: não existe
             re-anexar na mesma tratativa (0096 §5.1). */}
         <ReasonConfirmModal
            isOpen={!!fileToDelete}
            title="Excluir anexo"
            subtitle={fileToDelete?.file?.original_name ?? null}
            infoText={
               <>
                  O arquivo é <strong>apagado definitivamente</strong> e não pode
                  ser reenviado nesta tratativa — se precisar do documento
                  correto, registre uma nova tratativa com ele. O motivo abaixo
                  fica registrado no lugar do anexo, visível no histórico.
               </>
            }
            reasonLabel="Motivo da exclusão"
            placeholder="Ex.: Comprovante anexado no caso errado"
            minLength={ATTACHMENT_REASON_MIN}
            maxLength={ATTACHMENT_REASON_MAX}
            confirmLabel="Excluir anexo"
            submittingLabel="Excluindo…"
            isSubmitting={isDeletingFile}
            inputId="delete-dunning-file-reason"
            onCancel={() => setFileToDelete(null)}
            onConfirm={handleConfirmDeleteFile}
         />
      </>
   );
}

CaseDrawer.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   caseData: PropTypes.object,
   // eslint-disable-next-line react/forbid-prop-types
   events: PropTypes.array.isRequired,
   loading: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   stages: PropTypes.array.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   activeStages: PropTypes.array.isRequired,
   today: PropTypes.string.isRequired,
   canInteract: PropTypes.bool.isRequired,
   canUpdate: PropTypes.bool.isRequired,
   canExternal: PropTypes.bool.isRequired,
   canSettle: PropTypes.bool.isRequired,
   canAssign: PropTypes.bool.isRequired,
   canReconcile: PropTypes.bool.isRequired,
   canReadAll: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   awaitingCharges: PropTypes.object.isRequired,
   onClose: PropTypes.func.isRequired,
   onOpenCase: PropTypes.func.isRequired,
   onCaseUpdated: PropTypes.func.isRequired,
   onOpenCommit: PropTypes.func.isRequired,
   onOpenAssign: PropTypes.func.isRequired,
   onAskTransfer: PropTypes.func.isRequired,
   onSettleConfirmed: PropTypes.func.isRequired,
};
